import React from 'react';
import { Lightbulb, DollarSign, PiggyBank, BarChart2, TrendingUp, CreditCard, Layers } from 'lucide-react';

/**
 * Компонент с советами по управлению бюджетом
 */
const BudgetTips = ({ results, formData }) => {
  // Если нет результатов расчета, не показываем советы
  if (!results) return null;

  // Определяем, какие советы показывать в зависимости от состояния бюджета
  const getTipsByStatus = () => {
    switch (results.status) {
      case 'excellent':
        return [
          'Продолжайте придерживаться текущего распределения бюджета',
          'Рассмотрите возможность увеличения инвестиций для более быстрого роста капитала',
          'Изучите возможности дополнительных источников пассивного дохода',
          'Обновите финансовые цели, возможно, пора установить более амбициозные',
          'Делитесь своими финансовыми знаниями с близкими'
        ];
      case 'good':
        return [
          'У вас стабильный бюджет, но есть возможности для оптимизации',
          'Рассмотрите увеличение доли сбережений на 2-3%',
          'Пересмотрите необязательные расходы — возможно, есть что сократить',
          'Если у вас нет чрезвычайного фонда, начните его формировать',
          'Запланируйте ежеквартальный пересмотр бюджета для отслеживания прогресса'
        ];
      case 'fair':
        return [
          'Ваш бюджет сбалансирован, но имеет уязвимые места',
          'Сократите долю необязательных расходов на 5-10%',
          'Установите конкретные цели по увеличению дохода',
          'Создайте чрезвычайный фонд в размере минимум 3 месячных расходов',
          'Избегайте новых кредитов и займов до улучшения финансового положения'
        ];
      case 'poor':
        return [
          'Ваш бюджет требует серьезной корректировки',
          'Сделайте приоритетом сокращение необязательных расходов',
          'Рассмотрите возможности увеличения дохода, включая подработки',
          'Составьте план погашения существующих долгов',
          'Временно перейдите на "режим экономии" во всех категориях'
        ];
      default:
        return [
          'Регулярно отслеживайте свои доходы и расходы',
          'Создайте чрезвычайный фонд в размере 3-6 месячных расходов',
          'Старайтесь сберегать минимум 10% от дохода',
          'Пересматривайте бюджет каждые 3-6 месяцев'
        ];
    }
  };

  // Специальные советы в зависимости от характеристик бюджета
  const getSpecialTips = () => {
    const tips = [];
    
    // Советы по доходам
    if (results.totalIncome < 30000) {
      tips.push({
        category: 'income',
        icon: <DollarSign className="w-5 h-5 text-emerald-500" />,
        title: 'Увеличение дохода',
        content: 'При доходе ниже 30 000 ₽ рассмотрите возможности дополнительного заработка: фриланс, подработка в выходные, сдача в аренду имущества.'
      });
    }
    
    // Советы по расходам
    if (results.essentialRatio > 0.7) {
      tips.push({
        category: 'expenses',
        icon: <CreditCard className="w-5 h-5 text-amber-500" />,
        title: 'Высокая доля обязательных расходов',
        content: 'Обязательные расходы составляют более 70% бюджета. Рассмотрите возможности их оптимизации: более выгодная аренда, пересмотр тарифов ЖКХ, смена тарифа связи.'
      });
    }
    
    // Советы по сбережениям
    if (results.savingsRate < 0.1) {
      tips.push({
        category: 'savings',
        icon: <PiggyBank className="w-5 h-5 text-blue-500" />,
        title: 'Недостаточные сбережения',
        content: 'Доля сбережений меньше рекомендуемых 10%. Попробуйте метод "сначала заплати себе" — откладывайте часть дохода сразу после получения.'
      });
    }
    
    // Советы по чрезвычайному фонду
    if (results.emergencyFund.enabled) {
      const adequacy = results.emergencyFund.currentAmount / results.emergencyFund.targetAmount;
      if (adequacy < 0.3) {
        tips.push({
          category: 'emergency',
          icon: <Layers className="w-5 h-5 text-red-500" />,
          title: 'Недостаточный чрезвычайный фонд',
          content: 'Ваш чрезвычайный фонд составляет менее 30% от целевого размера. Рекомендуется увеличить долю сбережений для его скорейшего пополнения.'
        });
      }
    } else {
      tips.push({
        category: 'emergency',
        icon: <Layers className="w-5 h-5 text-red-500" />,
        title: 'Отсутствует чрезвычайный фонд',
        content: 'У вас не настроен чрезвычайный фонд. Рекомендуется создать его в размере 3-6 месячных расходов для финансовой безопасности.'
      });
    }
    
    // Советы по инвестициям
    if (!results.investments.enabled && results.balance > 10000) {
      tips.push({
        category: 'investments',
        icon: <TrendingUp className="w-5 h-5 text-purple-500" />,
        title: 'Возможность инвестирования',
        content: 'У вас положительный баланс, но нет настроенных инвестиций. Рассмотрите возможность долгосрочного инвестирования для роста капитала.'
      });
    }
    
    return tips;
  };

  const generalTips = getTipsByStatus();
  const specialTips = getSpecialTips();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-5 flex items-center">
        <Lightbulb className="w-6 h-6 mr-2 text-yellow-500" />
        Советы по управлению бюджетом
      </h2>

      {/* Общие советы на основе статуса бюджета */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          Персональные рекомендации
        </h3>
        <ul className="space-y-2 text-gray-600 dark:text-gray-400">
          {generalTips.map((tip, index) => (
            <li key={index} className="flex items-start">
              <span className="inline-block w-5 h-5 flex-shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs font-medium flex items-center justify-center mr-3 mt-0.5">
                {index + 1}
              </span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Специальные советы для конкретных ситуаций */}
      {specialTips.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
            Обратите внимание
          </h3>
          {specialTips.map((tip, index) => (
            <div 
              key={index}
              className={`p-4 rounded-lg border ${
                tip.category === 'income' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800' : 
                tip.category === 'expenses' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800' :
                tip.category === 'savings' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800' :
                tip.category === 'emergency' ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800' :
                'bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800'
              }`}
            >
              <div className="flex items-start">
                <div className="mr-3 mt-0.5 flex-shrink-0">
                  {tip.icon}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">{tip.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{tip.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Практические шаги по улучшению бюджета */}
      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
          Практические шаги по улучшению бюджета
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
              <DollarSign className="w-4 h-4 mr-1.5 text-emerald-500" />
              Оптимизация доходов
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside pl-1">
              <li>Изучите возможности повышения на текущей работе</li>
              <li>Развивайте дополнительные навыки для роста дохода</li>
              <li>Рассмотрите источники пассивного дохода</li>
              <li>Ищите дополнительные возможности заработка</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
              <CreditCard className="w-4 h-4 mr-1.5 text-amber-500" />
              Сокращение расходов
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside pl-1">
              <li>Проверьте подписки и регулярные платежи</li>
              <li>Сравнивайте цены перед покупкой</li>
              <li>Используйте кэшбек и бонусные программы</li>
              <li>Составляйте список покупок заранее</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
              <PiggyBank className="w-4 h-4 mr-1.5 text-blue-500" />
              Эффективные сбережения
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside pl-1">
              <li>Откладывайте 10-20% дохода автоматически</li>
              <li>Создайте отдельный счет для чрезвычайного фонда</li>
              <li>Ставьте конкретные финансовые цели с дедлайнами</li>
              <li>Используйте правило "24 часа" перед крупной покупкой</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
              <TrendingUp className="w-4 h-4 mr-1.5 text-purple-500" />
              Основы инвестирования
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside pl-1">
              <li>Начните с образования в финансовой сфере</li>
              <li>Используйте ИИС для налоговых вычетов</li>
              <li>Диверсифицируйте инвестиционный портфель</li>
              <li>Инвестируйте регулярно, а не разовыми суммами</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetTips;